/**
 * Executor Performance Tracker Component
 * Comprehensive visualization for supervisor to track executor productivity & quality
 * 
 * Features:
 * - Overview bar chart comparing all executors
 * - Leaderboard with rankings
 * - Individual executor cards with progress bars
 * - Drill-down modal for detailed metrics
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';

// Chart colors consistent with EMZI CI
const CHART_COLORS = {
  primary: '#0072E5',
  success: '#16A34A',
  warning: '#F59E0B',
  error: '#DC2626',
  purple: '#8B5CF6',
  cyan: '#06B6D4',
  pink: '#EC4899',
  indigo: '#6366F1'
};

// Render medal for top 3
const getMedal = (rank) => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
};

// Progress bar component - moved outside
const ProgressBar = ({ value, max = 100, color = CHART_COLORS.primary, label, showPercentage = true }) => (
  <div className="w-full">
    {label && (
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{label}</span>
        {showPercentage && <span className="font-bold">{value}%</span>}
      </div>
    )}
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="h-2 rounded-full transition-all duration-500"
        style={{ width: `${Math.min(value, max)}%`, backgroundColor: color }}
      />
    </div>
  </div>
);

// Custom tooltip for bar chart - moved outside
const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-bold text-gray-800 mb-2">{data.fullName || label}</p>
        <div className="space-y-1 text-sm">
          <p className="text-gray-600">📊 Submissions: <span className="font-bold">{data.submissions}</span></p>
          <p className="text-green-600">✅ Approved: <span className="font-bold">{data.approved}</span></p>
          <p className="text-yellow-600">⏳ Pending: <span className="font-bold">{data.pending}</span></p>
          <p className="text-purple-600">🎬 Videos: <span className="font-bold">{data.videos}</span></p>
        </div>
      </div>
    );
  }
  return null;
};

// Executor detail modal - moved outside
const ExecutorDetailModal = ({ executor, onClose }) => {
  if (!executor) return null;

  const pieData = [
    { name: 'Approved', value: executor.approved || 0, color: CHART_COLORS.success },
    { name: 'Pending', value: executor.pending || 0, color: CHART_COLORS.warning },
    { name: 'Changes Req.', value: executor.request_changes || 0, color: CHART_COLORS.error }
  ].filter(d => d.value > 0);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4 pb-24 sm:pb-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[75vh] sm:max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-2xl sm:rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl font-bold">
              {getMedal(executor.rank)}
            </div>
            <div>
              <h3 className="text-lg font-bold">{executor.executor_name || executor.user_id?.substring(0, 8) || 'Executor'}</h3>
              <p className="text-blue-100 text-sm">Rank #{executor.rank} • Quality Score: {executor.qualityScore}%</p>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="p-4 space-y-4">
          {/* Key Scores */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <div className="text-2xl font-black text-blue-600">{executor.qualityScore}%</div>
              <div className="text-xs text-blue-700 font-medium">Quality Score</div>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <div className="text-2xl font-black text-green-600">{executor.productivityScore}%</div>
              <div className="text-xs text-green-700 font-medium">Productivity</div>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="space-y-3">
            <ProgressBar 
              value={executor.approvalRate} 
              color={CHART_COLORS.success} 
              label="Approval Rate"
            />
            <ProgressBar 
              value={executor.videoCompletionRate} 
              color={CHART_COLORS.purple} 
              label="Video Completion"
            />
            <ProgressBar 
              value={executor.videoApprovalRate} 
              color={CHART_COLORS.cyan} 
              label="Video Approval Rate"
            />
          </div>

          {/* Submission Breakdown Pie Chart */}
          {pieData.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-bold text-gray-700 mb-3">📊 Submission Breakdown</h4>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Detailed Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-100 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-gray-800">{executor.total_submissions || 0}</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
            <div className="bg-green-100 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-green-700">{executor.approved || 0}</div>
              <div className="text-xs text-green-600">Approved</div>
            </div>
            <div className="bg-yellow-100 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-yellow-700">{executor.pending || 0}</div>
              <div className="text-xs text-yellow-600">Pending</div>
            </div>
            <div className="bg-orange-100 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-orange-700">{executor.request_changes || 0}</div>
              <div className="text-xs text-orange-600">Changes</div>
            </div>
            <div className="bg-purple-100 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-purple-700">{executor.videos_submitted || 0}</div>
              <div className="text-xs text-purple-600">Videos</div>
            </div>
            <div className="bg-cyan-100 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-cyan-700">{executor.videos_approved || 0}</div>
              <div className="text-xs text-cyan-600">V. Approved</div>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <div className="p-4 border-t">
          <Button 
            onClick={onClose}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3 rounded-xl"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const ExecutorPerformanceTracker = ({ executors = [], onExecutorClick }) => {
  const [selectedExecutor, setSelectedExecutor] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' | 'leaderboard' | 'cards'

  // Calculate enhanced metrics for each executor
  const enhancedExecutors = useMemo(() => {
    return executors.map((exec, index) => {
      const total = exec.total_submissions || 0;
      const approved = exec.approved || 0;
      const pending = exec.pending || 0;
      const requestChanges = exec.request_changes || 0;
      const videosSubmitted = exec.videos_submitted || 0;
      const videosApproved = exec.videos_approved || 0;
      
      // Calculate rates
      const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;
      const videoCompletionRate = approved > 0 ? Math.round((videosSubmitted / approved) * 100) : 0;
      const videoApprovalRate = videosSubmitted > 0 ? Math.round((videosApproved / videosSubmitted) * 100) : 0;
      
      // Quality score (weighted combination)
      const qualityScore = Math.round(
        (approvalRate * 0.4) + 
        (videoCompletionRate * 0.3) + 
        (videoApprovalRate * 0.3)
      );
      
      // Productivity score based on volume
      const maxSubmissions = Math.max(...executors.map(e => e.total_submissions || 0), 1);
      const productivityScore = Math.round((total / maxSubmissions) * 100);

      return {
        ...exec,
        approvalRate,
        videoCompletionRate,
        videoApprovalRate,
        qualityScore,
        productivityScore,
        rank: 0, // Will be calculated after sorting
        color: Object.values(CHART_COLORS)[index % Object.values(CHART_COLORS).length]
      };
    }).sort((a, b) => b.qualityScore - a.qualityScore)
      .map((exec, index) => ({ ...exec, rank: index + 1 }));
  }, [executors]);

  // Data for bar chart
  const barChartData = useMemo(() => {
    return enhancedExecutors.map(exec => ({
      name: exec.executor_name || exec.user_id?.substring(0, 8) || 'Unknown',
      fullName: exec.executor_name || exec.user_id || 'Unknown',
      submissions: exec.total_submissions || 0,
      approved: exec.approved || 0,
      pending: exec.pending || 0,
      videos: exec.videos_approved || 0,
      color: exec.color
    }));
  }, [enhancedExecutors]);

  const handleBarClick = (data) => {
    const exec = enhancedExecutors.find(e => e.user_id === data.fullName);
    if (exec) setSelectedExecutor(exec);
  };

  if (executors.length === 0) {
    return (
      <Card className="bg-white border-0 shadow-xl">
        <CardContent className="p-6 text-center text-gray-500">
          <div className="text-4xl mb-2">👥</div>
          <p>No executor data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white border-0 shadow-xl overflow-hidden">
        {/* Header with View Toggle - Compact */}
        <CardHeader className="px-3 py-2 border-b bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm text-gray-900 flex items-center font-bold">
              📊 Performance
              <Badge className="ml-1 bg-blue-100 text-blue-700 font-bold text-[10px] px-1.5 py-0">
                {executors.length}
              </Badge>
            </CardTitle>
            
            {/* View Toggle - Compact */}
            <div className="flex gap-0.5 bg-gray-100 rounded-md p-0.5">
              {[
                { id: 'overview', icon: '📈', label: '' },
                { id: 'leaderboard', icon: '🏆', label: '' },
                { id: 'cards', icon: '📋', label: '' }
              ].map(view => (
                <button
                  key={view.id}
                  onClick={() => setViewMode(view.id)}
                  className={`px-2 py-1 text-xs rounded transition-all ${
                    viewMode === view.id 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {view.icon}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-3">
          {/* Overview Bar Chart */}
          {viewMode === 'overview' && (
            <div className="space-y-3">
              {/* Summary Stats - 3 columns inline with correct names */}
              <div className="flex gap-2">
                <div className="flex-1 bg-blue-50 rounded-lg p-2 text-center">
                  <div className="text-base font-black text-blue-600">
                    {executors.reduce((sum, e) => sum + (e.total_submissions || 0), 0)}
                  </div>
                  <div className="text-[10px] text-blue-700">Submissions</div>
                </div>
                <div className="flex-1 bg-green-50 rounded-lg p-2 text-center">
                  <div className="text-base font-black text-green-600">
                    {executors.reduce((sum, e) => sum + (e.approved || 0), 0)}
                  </div>
                  <div className="text-[10px] text-green-700">SB Approved</div>
                </div>
                <div className="flex-1 bg-purple-50 rounded-lg p-2 text-center">
                  <div className="text-base font-black text-purple-600">
                    {executors.reduce((sum, e) => sum + (e.videos_approved || 0), 0)}
                  </div>
                  <div className="text-[10px] text-purple-700">Vid Approved</div>
                </div>
              </div>

              {/* Grouped Bar Chart - Submissions, Approved, Videos */}
              <div className="bg-gray-50 rounded-xl p-3">
                <h4 className="text-xs font-bold text-gray-700 mb-2">📊 Performance by Executor</h4>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={barChartData} margin={{ top: 5, right: 5, left: -15, bottom: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 10, fill: '#666' }}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis tick={{ fontSize: 10, fill: '#666' }} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Bar dataKey="submissions" fill="#3B82F6" name="Submissions" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="approved" fill="#16A34A" name="SB Approved" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="videos" fill="#8B5CF6" name="Vid Approved" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div className="flex justify-center gap-3 mt-2">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-sm"></div>
                    <span className="text-[9px] text-gray-500">Submissions</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-600 rounded-sm"></div>
                    <span className="text-[9px] text-gray-500">SB Approved</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-sm"></div>
                    <span className="text-[9px] text-gray-500">Vid Approved</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard View - Compact */}
          {viewMode === 'leaderboard' && (
            <div className="space-y-1.5">
              {enhancedExecutors.map((executor, index) => (
                <div
                  key={executor.user_id}
                  onClick={() => setSelectedExecutor(executor)}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    index === 0 ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200' :
                    index === 1 ? 'bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200' :
                    index === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200' :
                    'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {/* Rank */}
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                    index === 0 ? 'bg-yellow-400 text-yellow-900' :
                    index === 1 ? 'bg-gray-300 text-gray-700' :
                    index === 2 ? 'bg-orange-400 text-orange-900' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {getMedal(executor.rank)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-800 text-xs truncate">{executor.executor_name || executor.user_id?.substring(0, 8) || 'Executor'}</div>
                    <div className="flex gap-1 mt-0.5">
                      <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">
                        {executor.approvalRate}%
                      </Badge>
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1 py-0">
                        {executor.total_submissions}
                      </Badge>
                    </div>
                  </div>

                  {/* Quality Score */}
                  <div className="text-right">
                    <div className="text-lg font-black" style={{ color: executor.color }}>
                      {executor.qualityScore}
                    </div>
                    <div className="text-[10px] text-gray-400">Score</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cards View - Compact */}
          {viewMode === 'cards' && (
            <div className="grid grid-cols-2 gap-2">
              {enhancedExecutors.map((executor) => (
                <div
                  key={executor.user_id}
                  onClick={() => setSelectedExecutor(executor)}
                  className="bg-gray-50 rounded-lg p-2 cursor-pointer hover:shadow-lg transition-all border border-gray-100 hover:border-blue-200"
                >
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <div 
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: executor.color }}
                    >
                      {(executor.executor_name || executor.user_id || '?')?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-800 text-xs truncate">{executor.executor_name || executor.user_id?.substring(0, 8) || 'Executor'}</div>
                      <div className="text-[10px] text-gray-400">{getMedal(executor.rank)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-black" style={{ color: executor.color }}>
                        {executor.qualityScore}%
                      </div>
                    </div>
                  </div>

                  {/* Mini Stats - 3 columns */}
                  <div className="grid grid-cols-3 gap-1 mb-2">
                    <div className="text-center">
                      <div className="text-xs font-bold text-gray-800">{executor.total_submissions || 0}</div>
                      <div className="text-[9px] text-gray-400">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-bold text-green-600">{executor.approved || 0}</div>
                      <div className="text-[9px] text-gray-400">✅</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-bold text-purple-600">{executor.videos_approved || 0}</div>
                      <div className="text-[9px] text-gray-400">🎬</div>
                    </div>
                  </div>

                  {/* Progress Bars - Compact */}
                  <div className="space-y-1">
                    <ProgressBar value={executor.approvalRate} color={CHART_COLORS.success} label="" showPercentage={false} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selectedExecutor && (
        <ExecutorDetailModal 
          executor={selectedExecutor} 
          onClose={() => setSelectedExecutor(null)} 
        />
      )}
    </>
  );
};

export default ExecutorPerformanceTracker;
